import React, { useState, useRef, useEffect } from 'react';
import { ClickAwayListener, Grow, Paper, Popper } from '@mui/material';
import { useTranslation } from 'react-i18next';
import PrimaryButton from '../buttons/PrimaryButton';
import CheckBox from '../input/Checkbox';
import SearchComponent from '../input/SearchComponent';
import styles from './CheckboxDropdown.module.scss';

export type CheckboxDropdownOption<T = string> = {
    id: T;
    label: React.ReactElement | string;
    subLabel?: React.ReactElement | string;
    searchText?: string; // Optional search text if label is a React element
};

export type Props<T = string> = {
    options: CheckboxDropdownOption<T>[];
    selectedIds: T[];
    onSelectionChange: (selectedIds: T[]) => void;
    onConfirm?: (selectedIds: T[]) => void;
    searchPlaceholder?: string;
    confirmButtonText?: string;
    emptyMessage?: string;
    countLabel?: (count: number) => string;
    renderFooter?: (selectedCount: number, onConfirm: () => void) => React.ReactElement;
    filterFunction?: (option: CheckboxDropdownOption<T>, searchText: string) => boolean;
    testId?: string;
    showSelectAll?: boolean; // New prop to enable Select All option
};

const CheckboxDropdown = <T extends string | number = string>({
    options,
    selectedIds,
    onSelectionChange,
    onConfirm,
    searchPlaceholder,
    confirmButtonText = 'Add',
    emptyMessage,
    countLabel,
    renderFooter,
    filterFunction,
    testId = 'checkbox-dropdown',
    showSelectAll = false,
}: Props<T>): React.ReactElement => {
    const { t: txt } = useTranslation();
    const [open, setOpen] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [tempSelectedIds, setTempSelectedIds] = useState<T[]>(selectedIds);
    const anchorRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        setTempSelectedIds(selectedIds);
    }, [selectedIds]);

    const defaultFilter = (option: CheckboxDropdownOption<T>, query: string): boolean => {
        if (!query) return true;
        const searchQuery = query.toLowerCase();
        if (typeof option.label === 'string') {
            return option.label.toLowerCase().includes(searchQuery);
        }
        return option.searchText?.toLowerCase().includes(searchQuery) ?? false;
    };

    const filterFn = filterFunction || defaultFilter;
    const filteredOptions = options.filter(option => filterFn(option, searchText));

    const toggleOption = (id: T): void => {
        const isSelected = tempSelectedIds.includes(id);
        if (isSelected) {
            setTempSelectedIds(tempSelectedIds.filter(selectedId => selectedId !== id));
        } else {
            setTempSelectedIds([...tempSelectedIds, id]);
        }
    };

    const handleSelectAll = (): void => {
        const allFilteredIds = filteredOptions.map(option => option.id);
        const allSelected = allFilteredIds.every(id => tempSelectedIds.includes(id));

        if (allSelected) {
            // Deselect all filtered options
            setTempSelectedIds(tempSelectedIds.filter(id => !allFilteredIds.includes(id)));
        } else {
            // Select all filtered options
            const newSelectedIds = [...tempSelectedIds];
            allFilteredIds.forEach(id => {
                if (!newSelectedIds.includes(id)) {
                    newSelectedIds.push(id);
                }
            });
            setTempSelectedIds(newSelectedIds);
        }
    };

    const handleConfirm = (): void => {
        onSelectionChange(tempSelectedIds);
        if (onConfirm) {
            onConfirm(tempSelectedIds);
        }
        setOpen(false);
        setSearchText('');
    };

    const handleClose = (event?: Event | React.SyntheticEvent): void => {
        if (anchorRef.current && anchorRef.current.contains(event?.target as HTMLElement)) {
            return;
        }
        setOpen(false);
        setTempSelectedIds(selectedIds);
        setSearchText('');
    };

    const onSearchUpdate = (text: string): void => {
        setOpen(true);
        setSearchText(text);
    };

    const selectedCount = tempSelectedIds.length;
    const defaultCountText = `${selectedCount} ${selectedCount === 1 ? txt('DeviceSelected') : txt('DevicesSelected')}`;
    const countText = countLabel ? countLabel(selectedCount) : defaultCountText;

    const allFilteredIds = filteredOptions.map(option => option.id);
    const allFilteredSelected = allFilteredIds.length > 0 && allFilteredIds.every(id => tempSelectedIds.includes(id));

    return (
        <div className={styles.dropdownContainer}>
            <SearchComponent
                testId={`${testId}-search`}
                onChange={onSearchUpdate}
                onFocus={(): void => setOpen(true)}
                parentRef={anchorRef}
                expandable
                placeholder={searchPlaceholder}
                fullWidth
            />
            <Popper
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                placement="bottom-start"
                transition
                disablePortal
                className={styles.popper}
            >
                {({ TransitionProps }): React.ReactElement => (
                    <Grow {...TransitionProps}>
                        <Paper className={styles.dropdown}>
                            <ClickAwayListener onClickAway={handleClose}>
                                <div className={styles.dropdownContent}>
                                    <div className={styles.optionsList}>
                                        {showSelectAll && filteredOptions.length > 0 && (
                                            <div
                                                className={`${styles.optionItem} ${styles.selectAllOption}`}
                                                onClick={handleSelectAll}
                                                onKeyDown={(e): void => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault();
                                                        handleSelectAll();
                                                    }
                                                }}
                                                role="button"
                                                tabIndex={0}
                                            >
                                                <CheckBox
                                                    id="select-all-checkbox"
                                                    checked={allFilteredSelected}
                                                    onChange={handleSelectAll}
                                                    color="primary"
                                                    label={
                                                        <div className={styles.optionLabel}>
                                                            <div
                                                                className={`${styles.optionMainLabel} ${styles.selectAllLabel}`}
                                                            >
                                                                <strong>{txt('SelectAll')}</strong>
                                                            </div>
                                                        </div>
                                                    }
                                                    labelIsElement
                                                />
                                            </div>
                                        )}
                                        {filteredOptions.length > 0 ? (
                                            filteredOptions.map(option => {
                                                const isSelected = tempSelectedIds.includes(option.id);
                                                return (
                                                    <div
                                                        key={String(option.id)}
                                                        className={styles.optionItem}
                                                        onClick={(): void => toggleOption(option.id)}
                                                        onKeyDown={(e): void => {
                                                            if (e.key === 'Enter' || e.key === ' ') {
                                                                e.preventDefault();
                                                                toggleOption(option.id);
                                                            }
                                                        }}
                                                        role="button"
                                                        tabIndex={0}
                                                    >
                                                        <CheckBox
                                                            id={String(option.id)}
                                                            checked={isSelected}
                                                            onChange={(): void => toggleOption(option.id)}
                                                            color="primary"
                                                            label={
                                                                <div className={styles.optionLabel}>
                                                                    <div className={styles.optionMainLabel}>
                                                                        {option.label}
                                                                    </div>
                                                                    {option.subLabel && (
                                                                        <div className={styles.optionSubLabel}>
                                                                            {option.subLabel}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            }
                                                            labelIsElement
                                                        />
                                                    </div>
                                                );
                                            })
                                        ) : (
                                            <div className={styles.noOptions}>
                                                {emptyMessage || txt('NoAvailableOptions')}
                                            </div>
                                        )}
                                    </div>
                                    {renderFooter ? (
                                        renderFooter(selectedCount, handleConfirm)
                                    ) : (
                                        <div className={styles.footer}>
                                            <div className={styles.selectedCount}>
                                                <span
                                                    className={
                                                        selectedCount === 0
                                                            ? styles.selectedCountTextZero
                                                            : styles.selectedCountText
                                                    }
                                                >
                                                    {countText}
                                                </span>
                                            </div>
                                            <PrimaryButton
                                                title={confirmButtonText}
                                                color="primary"
                                                onClick={handleConfirm}
                                                disabled={selectedCount === 0}
                                                testId={`${testId}-confirm-button`}
                                            />
                                        </div>
                                    )}
                                </div>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>
        </div>
    );
};

export default CheckboxDropdown;
